import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  type?: 'website' | 'article' | 'service';
  lang?: 'ms' | 'en';
  serviceName?: string;
  serviceType?: string;
  priceRange?: string;
  location?: string;
}

const SEO = ({
  title,
  description,
  keywords,
  image = 'https://trhine.services/og-image.jpg',
  type = 'website',
  lang = 'ms',
  serviceName,
  serviceType,
  priceRange,
  location: _location = 'Selangor, Malaysia'
}: SEOProps) => {
  const { pathname } = useLocation();
  const siteUrl = 'https://trhine.services';
  const currentUrl = `${siteUrl}${pathname}`;

  // Default keywords optimized for Malaysian market
  const defaultKeywords = lang === 'ms'
    ? 'servis kereta, polish kereta, cuci fabrik, servis aircond, kawalan serangga, pembasmian kuman, servis rumah, Selangor, Shah Alam, Klang, Puchong'
    : 'car service, car polish, fabric cleaning, aircon service, pest control, sanitization, home service, Selangor, Shah Alam, Klang, Puchong';

  const combinedKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;

  // Generate structured data for LocalBusiness
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': siteUrl,
    'name': 'TRhine Services',
    'alternateName': 'TRhine',
    'description': description,
    'url': currentUrl,
    'telephone': '+60182331065',
    'priceRange': priceRange || 'RM50-RM500',
    'image': image,
    'logo': `${siteUrl}/logo.png`,
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': 'Selangor',
      'addressRegion': 'Selangor',
      'addressCountry': 'MY'
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': '3.0738',
      'longitude': '101.5183'
    },
    'areaServed': [
      {
        '@type': 'City',
        'name': 'Shah Alam'
      },
      {
        '@type': 'City',
        'name': 'Klang'
      },
      {
        '@type': 'City',
        'name': 'Puchong'
      },
      {
        '@type': 'City',
        'name': 'Petaling Jaya'
      },
      {
        '@type': 'City',
        'name': 'Kuala Lumpur'
      }
    ],
    'openingHoursSpecification': {
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
      ],
      'opens': '08:00',
      'closes': '20:00'
    },
    'sameAs': [
      'https://www.facebook.com/trhineservices',
      'https://www.instagram.com/trhineservices'
    ]
  };

  // Add Service schema if service-specific page
  const serviceSchema = serviceName ? {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': serviceType || serviceName,
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'TRhine Services'
    },
    'areaServed': 'Selangor, Malaysia',
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': serviceName,
      'itemListElement': [{
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': serviceName
        }
      }]
    }
  } : null;

  // Breadcrumb schema
  const breadcrumbSchema = pathname !== '/' ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': siteUrl
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': title,
        'item': currentUrl
      }
    ]
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={lang === 'ms' ? 'ms-MY' : 'en-US'} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={combinedKeywords} />

      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />

      {/* Alternate Language Tags */}
      <link rel="alternate" hrefLang="ms" href={currentUrl} />
      <link rel="alternate" hrefLang="en" href={currentUrl} />
      <link rel="alternate" hrefLang="x-default" href={currentUrl} />

      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="TRhine Services" />
      <meta property="og:locale" content={lang === 'ms' ? 'ms_MY' : 'en_US'} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#000000" />

      {/* Geo Tags */}
      <meta name="geo.region" content="MY-10" />
      <meta name="geo.placename" content="Selangor" />
      <meta name="geo.position" content="3.0738;101.5183" />
      <meta name="ICBM" content="3.0738, 101.5183" />

      {/* Business Information */}
      <meta name="contact" content="+60182331065" />
      <meta name="coverage" content="Selangor, Malaysia" />

      {/* Robots Meta */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {serviceSchema && (
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      )}

      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
